import { useCallback, useEffect, useRef, useState } from 'react';

export type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

interface AutosaveArgs {
  /** 已 hydrate（當前文章資料進 state）才允許自動儲存。 */
  enabled: boolean;
  /** 切換文章時重設基準用的 key（通常是文章 id）。 */
  resetKey: string;
  /** 目前可儲存內容的序列化字串，用來比對 dirty。 */
  serialized: string;
  /** 守門：false 時靜默跳過（例：排程刊登但未選時間）。 */
  guardOk: boolean;
  /** 靜默儲存：成功 resolve、失敗 reject（不可自行噴 toast）。 */
  save: () => Promise<unknown>;
  /** debounce 間隔，預設 1800ms。 */
  delay?: number;
}

export interface AutosaveResult {
  status: SaveStatus;
  savedAt: number | null;
  isDirty: boolean;
  saving: boolean;
  /** 立即儲存（清掉 debounce，馬上存一次）。 */
  flush: () => void;
  /**
   * 手動儲存成功後呼叫：把基準對齊「送出當下」的內容（snapshot），狀態標記為已儲存。
   * 未給 snapshot 時以目前內容為基準。若送出後又有新輸入，仍會維持 dirty 並排程自動儲存。
   */
  markSaved: (snapshot?: string) => void;
}

/** WordPress 式自動儲存：dirty 後 debounce 靜默儲存，不噴成功 toast。 */
export function useAutosave(args: AutosaveArgs): AutosaveResult {
  const { enabled, resetKey, serialized, guardOk, save, delay = 1800 } = args;
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const baseline = useRef<string | null>(null);
  const inFlight = useRef(false);
  const requeue = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const refs = useRef({ enabled, serialized, guardOk, save, delay });
  refs.current = { enabled, serialized, guardOk, save, delay };

  // 用 ref 持有實作，讓 doSave 保持 stable、避免 render 造成的 closure 過時。
  const doSave = useRef<() => Promise<void>>(async () => {});
  doSave.current = async () => {
    const r = refs.current;
    if (!r.enabled || !r.guardOk) return; // 守門：未 hydrate / 排程缺時間 → 靜默略過
    if (baseline.current === null || r.serialized === baseline.current) return;
    if (inFlight.current) {
      requeue.current = true; // 儲存中又有變更 → 完成後補存一次
      return;
    }
    inFlight.current = true;
    setStatus('saving');
    const snap = r.serialized;
    try {
      await r.save();
      baseline.current = snap;
      setSavedAt(Date.now());
      setStatus('saved');
    } catch {
      setStatus('error');
    } finally {
      inFlight.current = false;
      if (requeue.current) {
        requeue.current = false;
        void doSave.current();
      }
    }
  };

  // 切換文章 / 首次 hydrate：以目前內容為基準，不視為 dirty。
  useEffect(() => {
    if (!enabled) return;
    baseline.current = serialized;
    setStatus('idle');
    setSavedAt(null);
    // 只在切換文章 / 首次 hydrate 時重設基準，不因每次輸入而重設。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, enabled]);

  // 內容變更 → debounce 後自動儲存。
  useEffect(() => {
    if (!enabled || baseline.current === null || serialized === baseline.current) return;
    setStatus((s) => (s === 'saving' ? s : 'dirty'));
    clearTimeout(timer.current);
    timer.current = setTimeout(() => void doSave.current(), delay);
    return () => clearTimeout(timer.current);
  }, [serialized, enabled, delay]);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    void doSave.current();
  }, []);
  const markSaved = useCallback((snapshot?: string) => {
    const snap = snapshot ?? refs.current.serialized;
    baseline.current = snap;
    setSavedAt(Date.now());
    if (snap === refs.current.serialized) {
      clearTimeout(timer.current);
      setStatus('saved');
    } else {
      // 送出後又有變更 → 維持 dirty 並重新排程自動儲存
      setStatus('dirty');
      clearTimeout(timer.current);
      timer.current = setTimeout(() => void doSave.current(), refs.current.delay);
    }
  }, []);

  const isDirty = enabled && baseline.current !== null && serialized !== baseline.current;
  return { status, savedAt, isDirty, saving: status === 'saving', flush, markSaved };
}

/** 離開攔截：關閉分頁/重整跳原生確認；站內導航離開時 flush 一次自動儲存。 */
export function useLeaveGuard(active: boolean, flush: () => void): void {
  const activeRef = useRef(active);
  activeRef.current = active;
  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!activeRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (activeRef.current) flushRef.current(); // 離開編輯頁 → 先 flush，避免遺失
    };
  }, []);
}
